// src/lib/menage-instructions-api.js — CHANTIER 42 Phase 4 (ADR-302)
//
// Client des CONSIGNES INTERNES ménage (`menage_instructions`).
//
// ── ASYMÉTRIE ASSUMÉE (décision ADR-302) ─────────────────────────────────────
//   LECTURE  = SELECT RLS-direct (supabase-js). Les 4 policies SELECT de la
//              migration 20261104000001 sont l'arbitre : ce module ne CONFÈRE
//              aucun droit, il rend ce que la base laisse passer.
//   ÉCRITURE = endpoints service_role (`menage_instruction_save` /
//              `menage_instruction_archive` / `menage_instruction_delete`).
//              Aucune écriture RLS-directe n'existe
//              (REVOKE INSERT/UPDATE/DELETE FROM authenticated).
//
// ── DEUX GESTES DISTINCTS, DEUX PORTÉES DE RÔLE ─────────────────────────────
//   ARCHIVER  (réversible) : admin ET orchestrateur. La ligne reste en base.
//   SUPPRIMER (définitif)  : ADMIN SEUL. La ligne est RÉELLEMENT supprimée ;
//              seule persiste une trace « qui / quand », jamais le contenu.
//              Décision Dimitri : le corps est du texte libre, une donnée
//              personnelle de voyageur y sera écrite par erreur un jour, et il
//              doit pouvoir l'effacer lui-même (RGPD).
//
// ── TROIS ÉTATS DE LECTURE (les fetchs ne THROW JAMAIS — leçon R5) ───────────
//   Toute lecture renvoie `{ instructions, available, error }`. Trois états, et
//   PAS deux : confondre « pas de consigne » et « je n'ai pas pu lire » est
//   exactement le faux vide que cette phase prétend éviter.
//
//   | available | error | signification            | AGENT            | RÉDACTEUR          |
//   |-----------|-------|--------------------------|------------------|--------------------|
//   | true      | false | lecture RÉUSSIE          | rend la liste    | rend la liste      |
//   | false     | false | migration NON appliquée  | SILENCE TOTAL    | « module non actif »|
//   | true      | true  | lecture ÉCHOUÉE (réseau, | SILENCE TOTAL    | BANDEAU D'ERREUR   |
//   |           |       | 500, GRANT/RLS, filtre)  |                  | explicite          |
//
//   L'ASYMÉTRIE agent/rédacteur est volontaire :
//     - côté AGENT   : quel que soit l'échec ⇒ SILENCE (aucun bloc, aucune
//       pastille). Sur l'app terrain, un bandeau technique serait du bruit
//       anxiogène — et une consigne non lue n'est pas une consigne inventée.
//     - côté RÉDACTEUR : « aucune consigne » serait FAUX et pourrait faire
//       croire à une perte de données. Il DOIT voir la différence entre
//       « module non actif » et « lecture impossible ».
//   Jamais de `catch {}` muet : console.warn pour le cas « migration absente »,
//   console.error pour le reste. Les deux restent INVISIBLES dans l'UI agent.
//
// ── PAS DE CACHE MODULE-LEVEL (contrairement à menage-log-info-cache) ────────
//   Une consigne CIBLÉE ne doit JAMAIS survivre à un changement de compte dans
//   le même onglet. Chaque appel refait la requête ; l'appelant reset son état
//   sur `currentUser.email` (même posture que `guestTimes`, Lot 3).

import { supabase } from '../supabase'

const TABLE = 'menage_instructions'
const ENDPOINT = '/api/beds24-mapping'

// Colonnes lues — miroir de la whitelist serveur. Jamais `*`.
const COLS =
  'id, logement_id, target_agent_email, title, body, priority, created_by, created_at, updated_by, updated_at, archived_at, archived_by'

// La table n'existe pas encore (migration non appliquée) — PostgREST renvoie
// PGRST205 (« table absente du schema cache »), en direct SQL 42P01.
//
// ── DÉTECTION ÉTROITE, VOLONTAIREMENT (correctif QA B3) ─────────────────────
//   DEUX conditions CUMULATIVES : un code de RELATION ABSENTE **ET** un message
//   qui nomme explicitement `menage_instructions`.
//   Une détection large (« /does not exist/ » sur n'importe quel message)
//   MAQUILLERAIT un vrai bug de schéma en « module non actif » :
//     - « column "priority" does not exist »  (42703) → VRAI BUG, doit remonter ;
//     - « function foo() does not exist »     (42883) → VRAI BUG, doit remonter ;
//     - PGRST204 « Could not find the 'x' column … in the schema cache »
//                                                    → VRAI BUG, doit remonter.
//   Aucun de ces trois ne passe plus ici : ils tombent dans la branche ERREUR.
const MISSING_RELATION_CODES = new Set(['PGRST205', '42P01'])

export function isMissingRelation(error) {
  if (!error) return false
  if (!MISSING_RELATION_CODES.has(error.code || '')) return false
  // Le message doit nommer NOTRE table : une autre relation absente n'est pas
  // « le module consignes n'est pas actif », c'est une anomalie à remonter.
  const haystack = [error.message, error.details, error.hint].filter(Boolean).join(' ')
  return new RegExp(TABLE, 'i').test(haystack)
}

// Réponse fail-closed unique (jamais un throw sur le chemin de lecture).
function emptyResult(available, error = false) {
  return { instructions: [], available, error }
}

function handleReadError(error, where) {
  if (isMissingRelation(error)) {
    // eslint-disable-next-line no-console
    console.warn(`[consignes] module inactif (migration non appliquée) — ${where}`)
    return emptyResult(false, false)
  }
  // eslint-disable-next-line no-console
  console.error(`[consignes] lecture échouée — ${where}`, error?.message || error)
  // TROISIÈME ÉTAT (correctif QA B1) : la table EXISTE (ou l'erreur est d'une
  // autre nature) et la lecture a ÉCHOUÉ. On ne prétend NI que le module est
  // inactif, NI qu'il n'y a « aucune consigne » — les deux seraient FAUX. Le
  // rédacteur verra un bandeau d'erreur ; l'agent, lui, reste silencieux.
  return emptyResult(true, true)
}

// Tri d'affichage : « importante » d'abord, puis les plus récentes.
export function sortInstructions(rows) {
  return [...(rows || [])].sort((a, b) => {
    const pa = a?.priority === 'importante' ? 0 : 1
    const pb = b?.priority === 'importante' ? 0 : 1
    if (pa !== pb) return pa - pb
    return String(b?.created_at || '').localeCompare(String(a?.created_at || ''))
  })
}

// Dédoublonne par id (R8 : une même ligne peut satisfaire deux policies quand
// l'orchestrateur porte la double casquette ; PostgREST n'en renvoie qu'une,
// mais une UI qui fusionne deux fetchs doit se protéger).
export function dedupeById(rows) {
  const seen = new Set()
  const out = []
  for (const r of rows || []) {
    if (!r?.id || seen.has(r.id)) continue
    seen.add(r.id)
    out.push(r)
  }
  return out
}

/**
 * Consignes ACTIVES visibles par l'utilisateur courant sur une liste de
 * logements (vue AGENT / terrain).
 *
 * La RLS fait TOUT le cloisonnement : ce filtre `.in(logement_id)` est un
 * FILTRE D'AFFICHAGE (borner au jour affiché), jamais une garantie de sécurité.
 * Les consignes TRANSVERSES (logement_id null) sont récupérées en plus, car
 * elles ne matchent aucun logement.
 *
 * @returns {Promise<{instructions: Array, available: boolean, error: boolean}>}
 *          — ne throw jamais (cf. tableau des 3 états en tête de fichier).
 */
export async function fetchMyInstructions(logementIds = []) {
  const ids = [...new Set((logementIds || []).filter(Boolean))]
  try {
    let query = supabase.from(TABLE).select(COLS).is('archived_at', null)
    if (ids.length > 0) {
      // Logements affichés OU consigne transverse (sans logement).
      query = query.or(`logement_id.in.(${ids.join(',')}),logement_id.is.null`)
    } else {
      // Aucun logement à l'écran : seules les transverses ont du sens.
      query = query.is('logement_id', null)
    }
    const { data, error } = await query
    if (error) return handleReadError(error, 'fetchMyInstructions')
    return { instructions: sortInstructions(dedupeById(data)), available: true, error: false }
  } catch (e) {
    return handleReadError(e, 'fetchMyInstructions')
  }
}

/**
 * Réduit une liste de consignes en COMPTE par logement (alimente la pastille
 * des cartes de run — une seule requête par jour affiché, jamais une par carte).
 *
 * Une consigne TRANSVERSE (logement_id null) s'applique PARTOUT : elle est
 * comptée sur chacun des logements demandés.
 */
export function countByLogement(instructions, logementIds = []) {
  const ids = [...new Set((logementIds || []).filter(Boolean))]
  const counts = {}
  ids.forEach((id) => {
    counts[id] = 0
  })
  for (const ins of instructions || []) {
    if (!ins || ins.archived_at) continue
    if (!ins.logement_id) {
      ids.forEach((id) => {
        counts[id] += 1
      })
    } else if (counts[ins.logement_id] != null) {
      counts[ins.logement_id] += 1
    }
  }
  return counts
}

/**
 * Consignes visibles par un RÉDACTEUR (admin / orchestrateur) — inclut les
 * ARCHIVÉES (pilotage de l'historique + réactivation).
 *
 * @param {{logementId?: string}} opts — filtre d'affichage optionnel.
 * @returns {Promise<{instructions: Array, available: boolean, error: boolean}>}
 *          — ne throw jamais. `error:true` ⇒ l'écran DOIT afficher un bandeau
 *          d'erreur, jamais « aucune consigne » (correctif QA B1).
 */
export async function fetchInstructionsForScope({ logementId } = {}) {
  try {
    let query = supabase.from(TABLE).select(COLS)
    if (logementId) query = query.eq('logement_id', logementId)
    const { data, error } = await query
    if (error) return handleReadError(error, 'fetchInstructionsForScope')
    return { instructions: sortInstructions(dedupeById(data)), available: true, error: false }
  } catch (e) {
    return handleReadError(e, 'fetchInstructionsForScope')
  }
}

// En-têtes auth (Bearer JWT) — pattern repris de src/lib/menage-avis-api.js.
async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' }
}

// POST vers l'endpoint service_role. À la DIFFÉRENCE des lectures, l'écriture
// THROW en cas d'échec : le rédacteur DOIT savoir que sa consigne n'est pas
// partie (l'inverse serait un faux succès).
async function postAction(action, payload) {
  let res
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ action, ...payload }),
    })
  } catch (e) {
    throw new Error('Connexion impossible. Réessayez.')
  }
  const json = await res.json().catch(() => ({}))
  if (res.status === 503) {
    throw new Error("Module consignes non actif (migration non appliquée).")
  }
  if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`)
  return json
}

/**
 * Crée (sans `id`) ou édite (avec `id`) une consigne.
 * À l'édition, `logement_id` / `target_agent_email` sont IGNORÉS côté serveur
 * (colonnes immuables) — on ne les envoie donc pas.
 */
export async function saveInstruction({
  id,
  logementId,
  targetAgentEmail,
  title,
  body,
  priority,
}) {
  const payload = id
    ? { id, title: title || null, body, priority: priority || 'normale' }
    : {
        logement_id: logementId || null,
        target_agent_email: targetAgentEmail || null,
        title: title || null,
        body,
        priority: priority || 'normale',
      }
  return postAction('menage_instruction_save', payload)
}

/**
 * Désactive (archived=true) ou réactive (false) une consigne.
 * Geste RÉVERSIBLE, ouvert à l'admin ET à l'orchestrateur — à ne pas confondre
 * avec `deleteInstruction` (irréversible, ADMIN SEUL).
 */
export async function archiveInstruction(id, archived) {
  return postAction('menage_instruction_archive', { id, archived: !!archived })
}

/**
 * SUPPRESSION DÉFINITIVE d'une consigne — décision Dimitri (RGPD).
 *
 * Le corps est du TEXTE LIBRE : une donnée personnelle de voyageur y sera
 * écrite par erreur un jour. L'archivage ne l'efface pas — elle resterait en
 * base à vie. Il faut donc un chemin d'effacement réel, et Dimitri doit pouvoir
 * corriger lui-même sans intervention technique.
 *
 * GARDE-FOUS (portés SERVEUR, jamais par cet appel) :
 *   - ADMIN SEUL : l'action n'est dans AUCUN bucket de rôle du routeur → elle
 *     tombe sur `requireAdmin`. Ni l'agent, ni l'orchestrateur, ni le
 *     propriétaire ne peuvent l'appeler — y compris en REST forgé.
 *   - la ligne est RÉELLEMENT supprimée (DELETE service_role), pas archivée ;
 *   - une TRACE persiste (`menage_instruction_deletions`) : QUI et QUAND,
 *     JAMAIS le contenu supprimé.
 */
export async function deleteInstruction(id) {
  return postAction('menage_instruction_delete', { id })
}
